import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gamepad2,
  Check,
  Loader2,
  ArrowRight,
  ArrowLeft,
  User,
  CheckCircle,
  Sparkles,
  Clock,
  ExternalLink,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GameIcon } from '@/components/ui/GameIcon';
import {
  api,
  type GameAccount,
  type GameWithVerificationInfo,
  type MatchVerificationSession,
  type ProfileCodeVerificationSession,
} from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

const OAUTH_PROVIDER_NAMES: Record<string, string> = {
  riot: 'Riot Games',
  epic: 'Epic Games',
  steam: 'Steam',
  faceit: 'FACEIT',
};

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
  }),
};

const transition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user: privyUser } = usePrivy();
  const setUser = useAuthStore((s) => s.setUser);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isSavingName, setIsSavingName] = useState(false);

  // Step 1: Display name
  const [displayName, setDisplayName] = useState('');

  // Step 2: Game selection & linking
  const [availableGames, setAvailableGames] = useState<GameWithVerificationInfo[]>([]);
  const [linkedAccounts, setLinkedAccounts] = useState<GameAccount[]>([]);
  const [selectedGame, setSelectedGame] = useState('');
  const [gameUserId, setGameUserId] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Match verification state
  const [verificationSession, setVerificationSession] = useState<MatchVerificationSession | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Profile code verification state
  const [profileCodeSession, setProfileCodeSession] = useState<ProfileCodeVerificationSession | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [games, accounts] = await Promise.all([
        api.getGamesWithVerificationInfo(),
        api.getGameAccounts(),
      ]);
      setAvailableGames(games.filter(g => g.hasApiKey));
      setLinkedAccounts(accounts);
      if (accounts.length > 0) {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Failed to load onboarding data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Pre-fill display name from Privy
    if (privyUser?.email?.address) {
      setDisplayName(privyUser.email.address.split('@')[0]);
    } else if (privyUser?.google) {
      const google = privyUser.google as { name?: string };
      if (google.name) setDisplayName(google.name);
    }
  }, [privyUser]);

  const goNext = async () => {
    // Save display name when leaving step 0
    if (step === 0 && displayName.trim()) {
      setIsSavingName(true);
      try {
        const updatedUser = await api.updateProfile({ displayName: displayName.trim() });
        setUser(updatedUser);
      } catch (err) {
        console.error('Failed to save display name:', err);
      } finally {
        setIsSavingName(false);
      }
    }
    setDirection(1);
    setStep(s => s + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setStep(s => s - 1);
  };

  const selectedGameInfo = availableGames.find(g => g.gameCode === selectedGame);

  const handleOAuthLink = async (game: GameWithVerificationInfo) => {
    if (!game.oauthProvider) return;
    setIsLinking(true);
    setLinkError('');
    try {
      const authUrl = await api.startOAuth(game.oauthProvider, game.oauthGameParam);
      window.location.href = authUrl;
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to start OAuth');
      setIsLinking(false);
    }
  };

  const handleLinkAccount = async () => {
    if (!selectedGame || !gameUserId.trim()) return;

    setIsLinking(true);
    setLinkError('');
    try {
      const account = await api.linkGameAccount(selectedGame, gameUserId.trim(), gameUserId.trim());
      setLinkedAccounts(prev => [...prev, account]);
      setSelectedGame('');
      setGameUserId('');
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to link account');
    } finally {
      setIsLinking(false);
    }
  };

  const handleInitiateMatchVerification = async () => {
    if (!selectedGame || !gameUserId.trim()) return;

    setIsLinking(true);
    setLinkError('');
    try {
      const session = await api.initiateMatchVerification(selectedGame, gameUserId.trim());
      setVerificationSession(session);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to find account');
    } finally {
      setIsLinking(false);
    }
  };

  const handleConfirmMatchVerification = async () => {
    if (!verificationSession) return;

    setIsVerifying(true);
    setLinkError('');
    try {
      const account = await api.confirmMatchVerification(verificationSession.id);
      setLinkedAccounts(prev => [...prev, account]);
      setVerificationSession(null);
      setSelectedGame('');
      setGameUserId('');
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleInitiateProfileCodeVerification = async () => {
    if (!selectedGame || !gameUserId.trim()) return;

    setIsLinking(true);
    setLinkError('');
    try {
      const session = await api.initiateProfileCodeVerification(selectedGame, gameUserId.trim());
      setProfileCodeSession(session);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to find account');
    } finally {
      setIsLinking(false);
    }
  };

  const handleConfirmProfileCodeVerification = async () => {
    if (!profileCodeSession) return;

    setIsVerifying(true);
    setLinkError('');
    try {
      const account = await api.confirmProfileCodeVerification(profileCodeSession.id);
      setLinkedAccounts(prev => [...prev, account]);
      setProfileCodeSession(null);
      setSelectedGame('');
      setGameUserId('');
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFinish = () => {
    navigate('/dashboard', { replace: true });
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalSteps = 3;
  const unlinkedGames = availableGames.filter(
    g => !linkedAccounts.some(a => a.gameCode === g.gameCode)
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Progress bar */}
      <div className="fixed left-0 right-0 top-0 z-50 h-1 bg-muted">
        <motion.div
          className="h-full bg-gradient-to-r from-violet-500 to-blue-500"
          initial={{ width: '0%' }}
          animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
          transition={{ type: 'spring', stiffness: 200, damping: 30 }}
        />
      </div>

      {/* Step indicators */}
      <div className="mx-auto flex items-center gap-3 pt-8">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <motion.div
            key={i}
            className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
              i < step
                ? 'bg-primary text-primary-foreground'
                : i === step
                  ? 'bg-primary/20 text-primary ring-2 ring-primary'
                  : 'bg-muted text-muted-foreground'
            }`}
            animate={{ scale: i === step ? 1.1 : 1 }}
            transition={transition}
          >
            {i < step ? <Check className="h-4 w-4" /> : i + 1}
          </motion.div>
        ))}
      </div>

      {/* Content */}
      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait" custom={direction}>
            {/* Step 0: Welcome + Display Name */}
            {step === 0 && (
              <motion.div
                key="step-0"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                className="space-y-8"
              >
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                    className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-violet-500 to-blue-500"
                  >
                    <Sparkles className="h-10 w-10 text-white" />
                  </motion.div>
                  <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="font-heading text-3xl font-bold"
                  >
                    Welcome to PullUp
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-2 text-muted-foreground"
                  >
                    Let's set up your profile in just a few steps
                  </motion.p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-4 rounded-2xl border bg-card p-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">Choose your display name</p>
                      <p className="text-sm text-muted-foreground">This is how other players will see you</p>
                    </div>
                  </div>
                  <Input
                    autoFocus
                    placeholder="Enter your display name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && displayName.trim() && goNext()}
                    className="h-12 text-lg"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    size="lg"
                    className="w-full gap-2"
                    disabled={!displayName.trim() || isSavingName}
                    onClick={goNext}
                  >
                    {isSavingName ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {/* Step 1: Link Game Accounts */}
            {step === 1 && (
              <motion.div
                key="step-1"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                className="space-y-6"
              >
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                    className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500"
                  >
                    <Gamepad2 className="h-8 w-8 text-white" />
                  </motion.div>
                  <h1 className="font-heading text-2xl font-bold">Link Your Games</h1>
                  <p className="mt-2 text-muted-foreground">
                    Connect at least one game to start competing
                  </p>
                </div>

                {/* Linked accounts */}
                <AnimatePresence>
                  {linkedAccounts.map((account) => (
                    <motion.div
                      key={account.id}
                      initial={{ opacity: 0, scale: 0.8, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={transition}
                      className="flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/5 p-4"
                    >
                      <GameIcon gameCode={account.gameCode} size="sm" />
                      <div className="flex-1">
                        <p className="font-semibold">{account.gameUsername || account.username}</p>
                        <p className="text-sm text-muted-foreground">
                          {availableGames.find(g => g.gameCode === account.gameCode)?.displayName}
                        </p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Game selection grid */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">
                    {linkedAccounts.length > 0 ? 'Link more games (optional)' : 'Select a game'}
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {unlinkedGames.map((game) => (
                      <motion.button
                        key={game.gameCode}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          if (game.verificationMethod === 'oauth') {
                            handleOAuthLink(game);
                          } else {
                            setSelectedGame(prev => prev === game.gameCode ? '' : game.gameCode);
                            setLinkError('');
                            setGameUserId('');
                            setVerificationSession(null);
                            setProfileCodeSession(null);
                          }
                        }}
                        className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-all ${
                          selectedGame === game.gameCode
                            ? 'border-primary bg-primary/10 shadow-lg shadow-primary/10'
                            : 'border-border hover:border-primary/50 hover:bg-accent'
                        }`}
                      >
                        <GameIcon gameCode={game.gameCode} size="sm" />
                        <span className="text-xs font-medium leading-tight">{game.displayName}</span>
                        {game.verificationMethod === 'oauth' && (
                          <span className="text-[10px] text-muted-foreground">
                            <ExternalLink className="inline h-2.5 w-2.5" /> {OAUTH_PROVIDER_NAMES[game.oauthProvider || ''] || 'OAuth'}
                          </span>
                        )}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Input form for non-OAuth games */}
                <AnimatePresence>
                  {selectedGame && selectedGameInfo && !verificationSession && !profileCodeSession && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={transition}
                      className="space-y-3 overflow-hidden"
                    >
                      <div className="rounded-xl border bg-card p-4 space-y-3">
                        <p className="text-sm font-medium">
                          Enter your {selectedGameInfo.inputLabel || selectedGameInfo.displayName + ' username'}
                        </p>
                        <Input
                          autoFocus
                          placeholder={selectedGameInfo.inputPlaceholder || 'Your in-game username or ID'}
                          value={gameUserId}
                          onChange={(e) => {
                            setGameUserId(e.target.value);
                            setLinkError('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (selectedGameInfo.verificationMethod === 'match_verification') {
                                handleInitiateMatchVerification();
                              } else if (selectedGameInfo.verificationMethod === 'in_game_code') {
                                handleInitiateProfileCodeVerification();
                              } else {
                                handleLinkAccount();
                              }
                            }
                          }}
                          className="h-11"
                        />
                        {linkError && (
                          <p className="text-sm text-destructive">{linkError}</p>
                        )}
                        <Button
                          onClick={
                            selectedGameInfo.verificationMethod === 'match_verification'
                              ? handleInitiateMatchVerification
                              : selectedGameInfo.verificationMethod === 'in_game_code'
                                ? handleInitiateProfileCodeVerification
                                : handleLinkAccount
                          }
                          disabled={!gameUserId.trim() || isLinking}
                          className="w-full"
                        >
                          {isLinking ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : selectedGameInfo.verificationMethod === 'match_verification' || selectedGameInfo.verificationMethod === 'in_game_code' ? (
                            <Gamepad2 className="mr-2 h-4 w-4" />
                          ) : (
                            <Check className="mr-2 h-4 w-4" />
                          )}
                          {selectedGameInfo.verificationMethod === 'match_verification' || selectedGameInfo.verificationMethod === 'in_game_code'
                            ? 'Find Account'
                            : 'Link Account'}
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Match verification step 2: play and confirm */}
                <AnimatePresence>
                  {verificationSession && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={transition}
                      className="space-y-3 overflow-hidden"
                    >
                      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-amber-500" />
                          <p className="text-sm font-semibold">Account found: {verificationSession.gameUsername}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Play any match on {selectedGameInfo?.displayName}, then click Verify below.
                          Battle logs can take 2-10 minutes to update.
                        </p>
                        {linkError && (
                          <p className="text-sm text-destructive">{linkError}</p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            onClick={handleConfirmMatchVerification}
                            disabled={isVerifying}
                            className="flex-1"
                          >
                            {isVerifying ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="mr-2 h-4 w-4" />
                            )}
                            Verify
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setVerificationSession(null);
                              setLinkError('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Profile code verification step 2: set code in profile */}
                <AnimatePresence>
                  {profileCodeSession && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={transition}
                      className="space-y-3 overflow-hidden"
                    >
                      <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-blue-500" />
                          <p className="text-sm font-semibold">Account found: {profileCodeSession.gameUsername}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Set your {selectedGameInfo?.displayName} <strong>{profileCodeSession.profileField}</strong> to:
                        </p>
                        <div className="flex items-center justify-center rounded-lg bg-background border p-3">
                          <code className="text-lg font-bold tracking-widest text-primary">{profileCodeSession.code}</code>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Go to your Chess.com profile settings, set your Location to the code above, then click Verify. You can change it back after.
                        </p>
                        {linkError && (
                          <p className="text-sm text-destructive">{linkError}</p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            onClick={handleConfirmProfileCodeVerification}
                            disabled={isVerifying}
                            className="flex-1"
                          >
                            {isVerifying ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="mr-2 h-4 w-4" />
                            )}
                            Verify
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setProfileCodeSession(null);
                              setLinkError('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Navigation */}
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" size="lg" onClick={goBack} className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    size="lg"
                    className="flex-1 gap-2"
                    disabled={linkedAccounts.length === 0}
                    onClick={goNext}
                  >
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
                {linkedAccounts.length === 0 && (
                  <button
                    onClick={goNext}
                    className="mx-auto block text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Skip for now
                  </button>
                )}
              </motion.div>
            )}

            {/* Step 2: All Done */}
            {step === 2 && (
              <motion.div
                key="step-2"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                className="space-y-8 text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.1 }}
                  className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500"
                >
                  <Check className="h-12 w-12 text-white" />
                </motion.div>

                <div>
                  <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="font-heading text-3xl font-bold"
                  >
                    You're all set, {displayName}!
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-2 text-muted-foreground"
                  >
                    {linkedAccounts.length > 0
                      ? `${linkedAccounts.length} game${linkedAccounts.length > 1 ? 's' : ''} linked. Time to compete!`
                      : 'You can link game accounts anytime in Settings'}
                  </motion.p>
                </div>

                {linkedAccounts.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mx-auto flex flex-wrap justify-center gap-2"
                  >
                    {linkedAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center gap-2 rounded-full border bg-card px-4 py-2"
                      >
                        <GameIcon gameCode={account.gameCode} size="sm" className="h-6 w-6" />
                        <span className="text-sm font-medium">{account.gameUsername || account.username}</span>
                        <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                      </div>
                    ))}
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="space-y-3"
                >
                  <Button size="lg" className="w-full gap-2" onClick={handleFinish}>
                    Go to Dashboard
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  {linkedAccounts.length === 0 && (
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full gap-2"
                      onClick={goBack}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Go back and link a game
                    </Button>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
